import React from 'react'
import { getMostRecentFieldValue, getNameType } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { DataBlock, H3 } from '@fs/zion-ui'
import FieldWrapper from '../../ui/FieldWrapper'
import RowWrapper from '../../ui/RowWrapper'
import InfoSectionHeader from './InfoSectionHeader'
import HelpTooltip from '../../ui/HelpTooltip'
import FieldDisplay from './FieldDisplay'
import BottomSheetDivider from './BottomSheetDivider'
import Subsection from './Subsection'

// The intermediary element is the same as the child and there is only 1 child so only display one
const parentSameAsField = (parent, field) =>
  (parent?.subElements?.length === 1 && parent?.elementType === field?.fieldType) ||
  parent?.elementType?.startsWith('NAME_')

// Renders a single section of info (e.g. "Essential Info", "Age", etc.) about the selected index
// including all the subelements of "element" and any children wrapped in this component
const IndexInfoSection = ({
  element,
  onEditClick,
  slsTypes,
  isBottomSheet,
  showPrincipalName,
  hideHeader,
  children,
  disableEditButton,
  indented,
}) => {
  const [t] = useTranslation()
  const hasSubElements = (se) => se?.subElements?.length
  const fieldsWithChildren =
    element?.subElements?.filter((se) => {
      return !parentSameAsField(se, se?.subElements?.[0]) && hasSubElements(se)
    }) || []

  const fieldsWithoutChildren =
    element?.subElements
      ?.filter((se) => {
        return (se.subElements && parentSameAsField(se, se?.subElements?.[0])) || se.elementType === 'FIELD'
      })
      .reduce((acc, fieldElement) => {
        if (fieldElement.subElements) acc = acc.concat([...fieldElement.subElements])
        else acc.push(fieldElement)
        return acc
      }, []) || []

  const nameTypeSelectOptions = React.useMemo(
    () =>
      slsTypes?.names?.map((name) => {
        return {
          value: name.nameType,
          label: name.displayLabel,
        }
      }),
    [slsTypes?.names]
  )

  // Deal with the SLS data AGE parent -> AGE field and AGE field by itself as a child of PERSON.
  const age = element?.elementType === 'AGE' || element.fieldType === 'AGE'
  const ageField = (element?.elementType === 'AGE' && element?.subElements?.[0]) || element

  return (
    <>
      {!hideHeader && (
        <InfoSectionHeader
          element={
            element?.elementType !== 'NAME'
              ? element
              : { ...element, label: t('view-index.essential.title', 'Essential Information') }
          }
          onEditClick={onEditClick}
          disabled={disableEditButton}
          slsTypes={slsTypes}
          isBottomSheet={isBottomSheet}
          indented={indented}
          growX
        />
      )}
      <>
        {age && (
          <Subsection>
            <RowWrapper>
              <FieldDisplay field={ageField} value={getMostRecentFieldValue(ageField)} />
            </RowWrapper>
          </Subsection>
        )}
        {!age && (
          <>
            {showPrincipalName && (
              <RowWrapper>
                <FieldWrapper>
                  <div style={{ display: 'flex' }}>
                    <DataBlock inline heading={t('view-index.principal.title', 'Primary Name')} />
                    <HelpTooltip />
                  </div>
                </FieldWrapper>
              </RowWrapper>
            )}
            {element?.elementType === 'NAME' && (
              <FieldWrapper>
                <H3 size="H6">{getNameType(nameTypeSelectOptions, element)?.label}</H3>
              </FieldWrapper>
            )}
            {fieldsWithoutChildren && (
              <Subsection noPadding>
                <RowWrapper>
                  {fieldsWithoutChildren.map((field) => {
                    if (field.label === 'Event Type' || field.fieldType === 'NAME') return null
                    return <FieldDisplay field={field} value={getMostRecentFieldValue(field)} key={field.id} />
                  })}
                  {children}
                </RowWrapper>
              </Subsection>
            )}
            {fieldsWithChildren.map((field) => (
              <div key={field.id}>
                <FieldWrapper>
                  {field.label !== 'Event Type' && field.fieldType !== 'NAME' && (
                    <H3 size="H6" supplementary>{`${field.label}`}</H3>
                  )}
                </FieldWrapper>
                <Subsection>
                  <RowWrapper>
                    {field?.subElements?.map((se) => (
                      <FieldDisplay key={se.id} field={se} value={getMostRecentFieldValue(se)} />
                    ))}
                  </RowWrapper>
                </Subsection>
              </div>
            ))}
            {/* Display Relationships Section if connectedRelationships exist */}
            {element.connectedRelationships && element.connectedRelationships.length > 0 && (
              <Subsection>
                {/* Relationships Heading */}
                <FieldWrapper indent="0">
                  <H3 size="H6" supplementary>
                    {t('view-index.relationships', 'Relationships')}
                  </H3>
                </FieldWrapper>

                {/* Render connected relationships under Relationships heading */}
                <RowWrapper>
                  {element.connectedRelationships.map((relationship) => (
                    <FieldDisplay
                      key={relationship.id}
                      field={{ label: relationship.label }}
                      value={relationship.value}
                    />
                  ))}
                </RowWrapper>
              </Subsection>
            )}
            {isBottomSheet && element?.elementType !== 'NAME' && <BottomSheetDivider />}
          </>
        )}
      </>
    </>
  )
}

export default IndexInfoSection
